import React, { Suspense, useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router';
import { useSidePanel } from '../../contexts/SidePanelContext';
import { useMobileDetection } from '../ui/useMobileDetection';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import { ROUTES } from '../../constants/routes';
import { BowerNestLoader } from '../../components/ui/BowerNestLoader';

const AttachmentDetail = lazyWithRetry(() => import('../../pages/AttachmentDetail'));

interface UseOpenAttachmentOptions {
  /**
   * Opt in to the split view (large desktop only; falls back to the full page
   * below lg).
   *
   * ONLY correct when the main view is the attachment's OWN parent — i.e. the
   * note / protocol detail page, opening one of its own attachments from
   * the Attachments panel. That is the entire reason the side view exists: the
   * scanned page or recording sits beside the note it was extracted into, so
   * the two can be compared.
   *
   * Every other surface (sidebar tree, pins, recents, Cmd-K search, artifact
   * and collection tables, linked-entity chips) opens the full page instead —
   * a lone attachment in a narrow right-hand pane, with an unrelated page
   * beside it, is just a cramped detail view.
   */
  readonly inSideView?: boolean;
}

/**
 * Hook that provides a function to open an attachment.
 *
 * Defaults to the full-page `/attachments/:id` route. Pass
 * `{ inSideView: true }` only from a parent note / protocol opening its own
 * attachment — see `UseOpenAttachmentOptions.inSideView`.
 *
 * The full-page route is also reachable from the "Open full page" button inside
 * AttachmentDetail when it is rendered in split view.
 */
export function useOpenAttachment({ inSideView = false }: UseOpenAttachmentOptions = {}) {
  const navigate = useNavigate();
  const sidePanel = useSidePanel();
  const { isDesktop } = useMobileDetection();

  return useCallback(
    (attachmentId: string, navigateOptions?: NavigateOptions) => {
      // Split view needs both panes to fit: below lg (small desktop, tablet,
      // mobile) even the parent-note case falls back to the full-page route.
      // `navigateOptions` (the workspace's history-entry state) rides on the
      // full-page route only. Called without it: a plain navigate.
      if (!inSideView || !isDesktop) {
        if (navigateOptions) navigate(ROUTES.attachment(attachmentId), navigateOptions);
        else navigate(ROUTES.attachment(attachmentId));
        return;
      }
      sidePanel.onOpen(
        <Suspense fallback={<BowerNestLoader size="md" />}>
          <AttachmentDetail attachmentId={attachmentId} onClose={sidePanel.onClose} />
        </Suspense>,
      );
    },
    [inSideView, isDesktop, navigate, sidePanel],
  );
}
